import { useState, useRef, useEffect } from "react";
import { Mic, Square, Send, Trash2, Loader2 } from "lucide-react";
import { RecordingWaveform } from "./RecordingWaveform";

// Reusable voice-note recorder for private messages: record -> preview -> send.
// Calls onSend(blob) which should upload the audio. maxSecs auto-stops recording.
export function MessageVoiceRecorder({ onSend, maxSecs = 60, testid = "msg-voice" }) {
  const [recording, setRecording] = useState(false);
  const [secsLeft, setSecsLeft] = useState(maxSecs);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [waveStream, setWaveStream] = useState(null);
  const [sending, setSending] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const limitRef = useRef(null);
  const micOk = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window !== "undefined" && "MediaRecorder" in window;

  useEffect(() => () => { clearInterval(limitRef.current); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setWaveStream(null);
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (b.size) { setBlob(b); setPreviewUrl(URL.createObjectURL(b)); }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setWaveStream(stream);
      setSecsLeft(maxSecs);
      clearInterval(limitRef.current);
      limitRef.current = setInterval(() => setSecsLeft((s) => { if (s <= 1) { stopRec(); return 0; } return s - 1; }), 1000);
    } catch { setRecording(false); }
  };
  const stopRec = () => {
    clearInterval(limitRef.current);
    if (recRef.current && recRef.current.state === "recording") recRef.current.stop();
    setRecording(false);
  };
  const discard = () => { setBlob(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(""); };
  const send = async () => {
    if (!blob) return;
    setSending(true);
    try { await onSend(blob); discard(); } finally { setSending(false); }
  };

  if (!micOk) return null;

  if (blob) {
    return (
      <div className="flex items-center gap-2 flex-wrap bg-clinical/5 border border-clinical/20 rounded-lg p-2" data-testid={`${testid}-preview`}>
        <audio controls src={previewUrl} className="h-8 max-w-[200px]" data-testid={`${testid}-preview-audio`} />
        <button type="button" onClick={send} disabled={sending} data-testid={`${testid}-send`}
          className="inline-flex items-center gap-1.5 rounded-md bg-clinical text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
        </button>
        <button type="button" onClick={discard} disabled={sending} data-testid={`${testid}-discard`}
          className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-sm font-semibold text-muted2 hover:text-critical">
          <Trash2 className="w-4 h-4" /> Discard
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {recording && (
        <div className="flex-1 flex items-center gap-2 rounded-lg border border-critical/30 bg-critical/5 px-2.5 py-1.5" data-testid={`${testid}-recording-bar`}>
          <span className="inline-block w-2 h-2 rounded-full bg-critical animate-pulse" />
          <RecordingWaveform stream={waveStream} className="flex-1" />
          <span className="text-xs font-bold tabular-nums text-critical">{secsLeft}s</span>
        </div>
      )}
      <button type="button" onClick={recording ? stopRec : startRec} data-testid={`${testid}-record`}
        title={recording ? "Stop recording" : "Record a voice note"}
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold border transition-colors ${recording ? "bg-critical text-white border-critical animate-pulse" : "bg-white text-clinical border-clinical/30 hover:bg-clinical/5"}`}>
        {recording ? <><Square className="w-4 h-4" /> Stop ({secsLeft}s)</> : <Mic className="w-4 h-4" />}
      </button>
    </div>
  );
}
